import { useCallback, useState } from "react";
import {
  confirmAllCategorizations,
  type ConfirmAllItem,
  type ConfirmAllTarget,
} from "../api/transactions";

/** The distinct server sentences of the items confirm-all did not book. */
function failureSentences(results: ConfirmAllItem[]): string[] {
  const sentences = results
    .filter((r) => r.status !== "confirmed" && r.message)
    .map((r) => r.message ?? "");
  return [...new Set(sentences)];
}

export interface ConfirmAll {
  confirming: boolean;
  /** Confirm the targets; rejects when the whole request was refused. */
  run: () => Promise<void>;
}

/**
 * Confirm-all of exactly the shown targets (2.8 (5)). Each item not booked
 * hands its sentence to `onRefusedItems` once (every item LEDGER_BUSY is one
 * sentence); the list is fetched again after every answer. A second press
 * while one is in flight is ignored.
 */
export function useConfirmAll(
  clientId: number,
  targets: ConfirmAllTarget[],
  onUpdated: () => void,
  onRefusedItems: (sentences: string[]) => void,
): ConfirmAll {
  const [confirming, setConfirming] = useState(false);
  const run = useCallback(async () => {
    if (confirming || targets.length === 0) return;
    setConfirming(true);
    try {
      const result = await confirmAllCategorizations(clientId, targets);
      onRefusedItems(failureSentences(result.items));
      onUpdated();
    } finally {
      setConfirming(false);
    }
  }, [clientId, confirming, targets, onUpdated, onRefusedItems]);
  return { confirming, run };
}
